import type { AppsAppModel, AppsAppUsageModel, AppsPlatformModel } from '@customerio/hydra-ui-api';

/**
 * A workspace app (mobile app registration). An app owns a name, the default
 * flag that decides which app a new push message starts with, and one platform
 * entry per mobile platform it is set up for.
 */
export type App = AppsAppModel;

/**
 * One platform of an app: the identifier a device reports (a bundle id on iOS,
 * an application id on Android) and the credential that platform sends
 * through. `credential_id` is null while the platform is registered but has no
 * key yet, which is what makes it unable to send.
 */
export type AppPlatform = AppsPlatformModel;

/** What an app is still used by, read before deleting it. */
export type AppUsage = AppsAppUsageModel;

/**
 * The fields the supplementary readers need: the delivery detail resolves a
 * push delivery's `app_id` to its app name, the Design Studio inbox styles
 * section names the default app the visual inbox delivers to (INAPP-14649), and
 * the people condition builder's `app_identifier` picker builds its options
 * from each app's per-platform identifiers.
 */
export type PushApp = Pick<App, 'id' | 'name' | 'is_default' | 'platforms'>;

// The active reads sit under the `['workspace', id, …]` subtree so a workspace
// switch's `removeQueries({ queryKey: ['workspace', prevId] })` evicts them. The
// no-workspace sentinel sits outside that namespace and never receives data (its
// query uses `skipToken`), so there is nothing to evict there.
export const appKeys = {
  list: (workspaceId: number) => ['workspace', workspaceId, 'app', 'list'] as const,
  detail: (workspaceId: number, appId: number) => ['workspace', workspaceId, 'app', 'detail', appId] as const,
  usage: (workspaceId: number, appId: number) => ['workspace', workspaceId, 'app', 'usage', appId] as const,
  noWorkspace: () => ['app', 'no-workspace'] as const,
};
